
import "@/pdfjs-dist/es5/build/pdf";
import * as pdfApp from "@/pdfjs-dist/lib/web/app";
import { AppOptions } from "@/pdfjs-dist/lib/web/app_options";
import "@/pdfjs-dist/lib/web/genericcom";
import "@/pdfjs-dist/lib/web/pdf_print_service";
import "@/pdfjs-dist/build/pdf.worker.entry";
import "@/sass/icons.scss";
import "@/sass/index.scss";

import {
  ToolbarConfig,
  Theme,
  ToolbarIdConfig,
  PageScale,
  IFile,
  IAdsibInformation,
  MODE_AGETIC,
} from "@/types";
import getAppConfig from "@/utils/pdf-config";
import { PDF_FILE_INPUT_ID } from "@/utils/constants";
import locale from "@/utils/locale";
import {
  getToolbarConfigValue,
  toolbarConfig,
  viewButton,
  viewValorLegal,
} from "@/utils/toolbar-config";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  reactive,
  ref,
} from "vue";
// import LoadingComponent from "./load/loading.component.vue";
import EyeIcon from "./icons/EyeIcon.vue";
import EyeSlashIcon from "./icons/EyeSlashIcon.vue";

import { Toasts } from "./toast/toast";

import {
  onFirmarDocumentoApi,
  onGetInformationFilesApi,
  onObtenerValorLegalDocumentoAdsibApi,
  onObtenerValorLegalDocumentoAgeticApi,
  onGetInformationADSIBApi,
  onSincronizarValorLegalDocumentoAgeticApi,
  onSincronizarValorLegalDocumentoAdsibApi,
} from "@/api/service.api";
import { generateUrlWaterMarker, formatSizeUnits } from "../utils/convert";
import {
  LoginDigital,
  // ListenPopupDigitalCitizenchip,
  validateDocument,
} from "vue-login-adds";
import { DateTime } from "luxon";
import {
  DATE_FORMAT_HUMAN,
  DATE_TIME_FULL_HUMAN,
} from "@/constants/date.const";
import LoaderComponent from "./load/loaderComponent.vue";
import OverlayContainer from "./OverlayContainer.vue";
import Toolbar from "./Toolbar.vue";
import SidebarLeft from "./SidebarLeft.vue";
import ErrorWrapper from "./ErrorWrapper.vue";
import AdsibInformation from "./AdsibInformation.vue";
if (AppOptions) AppOptions.set("defaultUrl", null);

const themeCacheKey = "vue-pdf-app-theme";
const errorHandler = console.error.bind(console);

export default defineComponent({
  name: "PdfViewerComponent",
  components: {
    AdsibInformation,
    // LoadingComponent,
    LoaderComponent,
    EyeIcon,
    EyeSlashIcon,
    OverlayContainer,
    SidebarLeft,
    Toolbar,
    ErrorWrapper,
  },
  emits: [
    "after-created",
    "pages-rendered",
    "open",
    "fileSign",
    "fileApprove",
    "deleteFile",
    "pages-rendered",
    "update:theme",
  ],
  props: {
    signature: {
      default: () => viewValorLegal,
      type: Object as PropType<ToolbarConfig>,
    },
    viewSignature: {
      default: () => viewValorLegal,
      type: Object as PropType<ToolbarConfig>,
    },
    viewButton: {
      default: () => viewButton,
      type: Object as PropType<ToolbarConfig>,
    },
    config: {
      default: () => toolbarConfig,
      type: Object as PropType<ToolbarConfig>,
    },
    files: { type: Object as PropType<IFile[]>, default: [] },
    token: {
      type: String,
      default: "",
    },
    tokenAgetic: {
      type: String,
      default: "",
    },
    modeAgetic: {
      type: String as PropType<MODE_AGETIC>,
      default: MODE_AGETIC.DEV,
    },
    msAdsib: {
      type: String,
      default: "https://ms-adsib.dev.mp.gob.bo",
    },
    msAgetic: {
      type: String,
      default: "https://ms-agetic.mp.gob.bo",
    },
    msSeguridad: {
      type: String,
      default: "https://ms-seguridad.dev.mp.gob.bo",
    },
    footerText: {
      type: String,
      default: "Copyright © FgePdfVue 2023",
    },
    footerVisible: {
      type: Boolean,
      default: false,
    },
    waterMarkerActive: {
      type: Boolean,
      default: false,
    },
    waterMarkerText: {
      type: String,
      default: "SIN VALOR LEGAL",
    },
    disableButtonSignAutomatic: {
      type: Boolean,
      default: false,
    },
    verifyFunctionary: {
      type: Boolean,
      default: false,
    },
    title: { type: Boolean, default: () => false },
    toastEnabled: { type: Boolean, default: () => true },
    buttonDeleteFile: { type: Boolean, default: () => true },
    buttonSyncFile: { type: Boolean, default: () => false },
    enableInformationFile: { type: Boolean, default: () => false },
    // pendiente a borrar
    fileName: String,
    pdf: { type: [String, ArrayBuffer] },

    theme: String as PropType<Theme>,
    idConfig: {
      type: Object as PropType<ToolbarIdConfig>,
      default: () => toolbarConfig,
    },
    pageScale: [Number, String] as PropType<PageScale>,
    pageNumber: Number,
  },
  setup(props, ctx) {
    const pageScaleCustom = ref(1);
    const aprobadores = ref([]);
    const defaultLocale = ref(JSON.stringify(locale));
    const isOpenHandlerBinded = ref(false);
    const isSidebarHidden = ref(true);
    const isFindbarHidden = ref(true);
    const cacheTheme = ref(
      window.localStorage.getItem(themeCacheKey) as Theme | null
    );
    const viewSidebar = reactive({
      fileSelect: undefined,
      files: false,
      signatures: false,
    });

    const isSidebarToolbarHidden = computed(() => {
      const idConfig = props.idConfig as ToolbarIdConfig;
      const isCustomToolbar =
        idConfig?.viewAttachments &&
        idConfig?.viewOutline &&
        idConfig?.viewThumbnail;
      return isCustomToolbar || !props.config.sidebar;
    });

    const isToolbarHidden = computed(() => {
      if (props.config.toolbar === false) return "zero-top";
      return "";
    });

    const localTheme = computed<Theme>(() => {
      if (props.theme) return props.theme;
      if (cacheTheme.value) return cacheTheme.value;
      const prefersTheme = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue("content")
        .replace(/"/g, "") as Theme;
      if (["light", "dark"].includes(prefersTheme)) return prefersTheme;
      return "dark";
    });

    const slotProps = computed(() => {
      return {
        toggleTheme: toggleTheme,
        isSidebarHidden: isSidebarHidden.value,
        isFindbarHidden: isFindbarHidden.value,
      };
    });

    const toggleButtonUnmount = ref<Function>();
    const findbarButtonUnmount = ref<Function>();
    const fileInputUnmount = ref<Function>();
    const printContainerUnmount = ref<Function>();
    onBeforeUnmount(() => {
      destroyPdf();
      if (toggleButtonUnmount.value) {
        toggleButtonUnmount.value();
      }
      if (findbarButtonUnmount.value) {
        findbarButtonUnmount.value();
      }
      if (fileInputUnmount.value) {
        fileInputUnmount.value();
      }
      if (printContainerUnmount.value) {
        printContainerUnmount.value();
      }
    });

    const pdfPrint = window.print.bind(window);
    window.print = (window as any).__nativePrint__ || pdfPrint;
    window.print = pdfPrint;

    pdfApp.PDFViewerApplication.isViewerEmbedded = !props.title;
    ctx.emit("after-created", pdfApp.PDFViewerApplication);

    const loading = ref(false);
    const ciudadaniaDigital = ref(false);

    const toasts = new Toasts({
      width: 300,
      timing: "ease",
      duration: ".5s",
      dimOld: false,
      position: "top-right", // top-left | top-center | top-right | bottom-left | bottom-center | bottom-right
    });

    const adsibInformation = ref<IAdsibInformation | null>(null);
    onMounted(() => {
      loading.value = true;
      ciudadaniaDigital.value =
        localStorage.getItem("digital") !== undefined &&
        localStorage.getItem("digital") != null;
      addPrintContainer();
      if (props.signature.adsib) {
        onGetInformationADSIBApi({
          body: {
            token: props.token,
            url: props.msSeguridad,
          },
        })
          .then((data) => {
            if (!data.error) adsibInformation.value = data.response;
          })
          .catch((e) => {
            adsibInformation.value = null;
          });
      }
      onLoadFiles();
      const config = getAppConfig(props.idConfig);
      if (pdfApp.PDFViewerApplication) {
        pdfApp.PDFViewerApplication.run(config);
        pdfApp.PDFViewerApplication.initializedPromise
          .then(setDefaultPageScale)
          .then(open)
          .then(bindSidebarToggleEvents)
          .then(bindFindbarToggleEvents)
          .catch(errorHandler);
      }
    });

    function bindSidebarToggleEvents() {
      const config = getAppConfig(props.idConfig);
      const toggleButton = config.sidebar.toggleButton;
      const handler = checkSidebarVisibility;
      toggleButton?.addEventListener("click", handler);
      toggleButtonUnmount.value = () => {
        toggleButton?.removeEventListener("click", handler);
      };
    }

    function bindFindbarToggleEvents() {
      const config = getAppConfig(props.idConfig);
      const toggleButton = config.findBar.toggleButton;
      const handler = checkFindbarVisibility;
      toggleButton?.addEventListener("click", handler);
      findbarButtonUnmount.value = () => {
        toggleButton?.removeEventListener("click", handler);
      };
    }

    function bindOpenHandler() {
      if (isOpenHandlerBinded.value) return;
      const fileInput = document.getElementById(PDF_FILE_INPUT_ID);
      const fileInputHandler = async () => {
        await pdfApp.PDFViewerApplication.pdfLoadingTask?.promise;
        openDocument();
      };
      fileInput?.addEventListener("change", fileInputHandler);
      fileInputUnmount.value = () => {
        fileInput?.removeEventListener("change", fileInputHandler);
      };
      isOpenHandlerBinded.value = true;
    }

    function open() {
      clearCacheTimeout();

      if (!pdfApp.PDFViewerApplication) return;
      if (!viewSidebar.fileSelect?.pdf) {
        pdfApp.PDFViewerApplication.close();
      } else {
        loading.value = true;
        pdfApp.PDFViewerApplication.open(viewSidebar.fileSelect.pdf)
          .then(() => {
            if (props.pageNumber) {
              setTimeout(
                () => (pdfApp.PDFViewerApplication.page = props.pageNumber)
              );
            }
            return pdfApp.PDFViewerApplication.pdfDocument?.getMetadata();
          })
          .then(
            (fileMetadata: { contentDispositionFilename: null | string }) => {
              pdfApp.PDFViewerApplication.contentDispositionFilename =
                props.fileName || fileMetadata.contentDispositionFilename;
              ctx.emit("pages-rendered", pdfApp.PDFViewerApplication);
              // onGetValueLegalCiudadaniaDigital();
              // onGetValueLegalAdsib();
            }
          )
          .catch(errorHandler)
          .finally(() => {
            loading.value = false;
          });
      }
    }

    function checkSidebarVisibility() {
      const sidebar = pdfApp.PDFViewerApplication?.pdfSidebar;
      isSidebarHidden.value = !(sidebar && sidebar.isOpen);
    }

    function checkFindbarVisibility() {
      const findbar = pdfApp.PDFViewerApplication?.findBar;
      isFindbarHidden.value = !(findbar && findbar.opened);
    }

    async function openDocument() {
      resetLoadingBar();
      ctx.emit("open", pdfApp.PDFViewerApplication);
      loading.value = true;
      if (pdfApp.PDFViewerApplication?.pdfViewer?.pagesPromise) {
        await pdfApp.PDFViewerApplication.pdfViewer.pagesPromise.catch(
          errorHandler
        );
        if (props.pageNumber) {
          setTimeout(
            () => (pdfApp.PDFViewerApplication.page = props.pageNumber)
          );
        }
        checkSidebarVisibility();
        checkFindbarVisibility();
        ctx.emit("pages-rendered", pdfApp.PDFViewerApplication);
        loading.value = false;
      }
    }

    function addPrintContainer() {
      const printElId = "printContainer";
      const el = document.createElement("div");
      el.id = printElId;
      document.body.appendChild(el);
      const styleEl = document.createElement("style");
      styleEl.type = "text/css";
      styleEl.innerHTML = `
      @media print {
        body > *:not(#printContainer) {
          display: none !important;
        }
      }`;
      document.head.appendChild(styleEl);
      printContainerUnmount.value = () => {
        document.body.removeChild(el);
        document.head.removeChild(styleEl);
      };
    }

    function destroyPdf(): void {
      clearCacheTimeout();
      pdfApp.PDFViewerApplication.unbindEvents();
      pdfApp.PDFViewerApplication.unbindWindowEvents();
      pdfApp.PDFViewerApplication.pdfDocument?.destroy();
      const el = document.getElementById(PDF_FILE_INPUT_ID);
      el && el.remove();
      // __nativePrint__ is assigned in pdf_print_service.js
      window.print = (window as any).__nativePrint__ || window.print;
    }

    function toggleTheme() {
      const newTheme = localTheme.value === "dark" ? "light" : "dark";
      ctx.emit("update:theme", newTheme);
      cacheTheme.value = newTheme;
      window.localStorage.setItem(themeCacheKey, newTheme);
    }

    function clearCacheTimeout() {
      const cacheTimeoutId =
        pdfApp.PDFViewerApplication.pdfRenderingQueue?.idleTimeout;
      clearTimeout(cacheTimeoutId);
    }

    function showElem(
      defaultToolbarPath: string,
      customToolbarElem?: keyof ToolbarIdConfig
    ): boolean {
      if (customToolbarElem && props.idConfig) {
        return !props.idConfig[customToolbarElem];
      }
      return !(
        getToolbarConfigValue(props.config, defaultToolbarPath) === false
      );
    }

    function setDefaultPageScale() {
      props.pageScale && AppOptions.set("defaultZoomValue", props.pageScale);
    }

    function resetLoadingBar() {
      pdfApp.PDFViewerApplication.loadingBar.show();
      pdfApp.PDFViewerApplication.loadingBar.percent = 0;
    }

    const data_items = reactive({
      adsib: [],
      adsibLoading: false,
      agetic: [],
      ageticLoading: false,
    });

    const items = computed(() => {
      return {
        adsib: [
          ...(data_items.adsib.find(
            (data) => data?.documentId === viewSidebar.fileSelect.id
          )?.verification ?? []),
        ],
        agetic: [
          ...(data_items.agetic.find(
            (data) => data?.documentId === viewSidebar.fileSelect.id
          )?.verification?.registros ?? []),
        ],
      };
    });

    const disabledButtonLegalComputed = computed(() => {
      const type = valorLegal.adsib
        ? "adsib"
        : valorLegal.agetic
        ? "agetic"
        : undefined;

      if (!props.disableButtonSignAutomatic) return false;
      if (items.value.adsib.length == 0 && items.value.agetic.length == 0) {
        return false;
      }

      switch (type) {
        case "agetic":
          return items.value.adsib.length > 0;
        case "adsib":
          return items.value.agetic.length > 0;
      }
    });

    const valorLegal = reactive({
      agetic: false,
      adsib: false,
    });

    const loader = reactive({
      message: "",
      adsib: false,
      agetic: false,
    });

    // #region servicios para adsib
    const onFirmar = async () => {
      try {
        onModal(false);
        loader.adsib = true;
        loader.message = "Firmando...";
        const result = await onFirmarDocumentoApi({
          body: {
            document_id: props.files.map((data) => data.id),
            pin: pin.value,
            token: props.token,
            url: props.msAdsib,
          },
        });
        if (!result.error) {
          pin.value = "";
          ctx.emit("fileSign", { result });
          onGetValueLegalAdsib(true);
          if (props.toastEnabled) {
            toasts.push({
              title: "Operación Exitosa",
              content: "Documentos firmados exitosamente",
              style: "success",
            });
          }
        }
      } catch (e) {
        ctx.emit("fileSign", { e });
        if (props.toastEnabled) {
          toasts.push({
            title: "Operación Fallida",
            content: e.message,
            style: "error",
          });
        }
      } finally {
        loader.adsib = false;
        loader.message = "";
      }
    };

    const onGetValueLegalAdsib = async (force = false) => {
      try {
        if (!props.viewSignature.adsib) return;
        if (!viewSidebar.fileSelect?.id) return;
        const find = data_items.adsib.findIndex(
          (item) => item?.documentId === viewSidebar.fileSelect.id
        );
        if (find != -1 && !force) return;
        data_items.adsibLoading = true;
        const result = await onObtenerValorLegalDocumentoAdsibApi({
          body: {
            document_ids: [viewSidebar.fileSelect.id],
            url: props.msAdsib,
            token: props.token,
          },
        });
        if (!result.error) {
          const index = data_items.adsib.findIndex(
            (item) => item.documentId === viewSidebar.fileSelect.id
          );
          if (index !== -1) data_items.adsib[index] = result.response[0];
          else data_items.adsib.push(result.response[0]);
        }
      } catch (e) {
        toasts.push({
          title: "Operación Fallida",
          content:
            "No se pudo obtener el valor legal del documento legal(ADSIB)",
          style: "error",
        });
      } finally {
        data_items.adsibLoading = false;
      }
    };
    // #endregion servicios para adsib

    // #region servicios para ciudadania digital
    const loadingAction = reactive({
      login_agetic: false,
      sync_file: false,
    });
    const modalSync = ref(false);
    // const tokenAgetic = ref<any>(null);
    const onLoginCiudadaniaDigital = async () => {
      const result = await LoginDigital({
        openWindows: false,
        tokenMsSeguridad: props.token,
        verifyFunctionary: props.verifyFunctionary
      });
      if (result) {
        if (result?.error) {
          toasts.push({
            title: "Operación Fallida",
            content:
              "El usuario de Ciudadanía Digital no coincide con el usuario registrado en el sistema",
            style: "error",
          });
          localStorage.removeItem("digital");
          localStorage.removeItem("dataAgetic");
        } else ciudadaniaDigital.value = true;
      } else ciudadaniaDigital.value = false;
    };

    const onAprobarCiudadania = async () => {
      const result = await validateDocument({
        openWindows: false,
        documentId: viewSidebar.fileSelect.id,
      });
      if (result && !result?.error) {
        onGetValueLegalCiudadaniaDigital(true);
        if (props.toastEnabled) {
          toasts.push({
            title: "Operación Exitosa",
            content: "Documento aprobado exitosamente",
            style: "success",
          });
        }
        ctx.emit("fileApprove", {
          documentId: viewSidebar.fileSelect.id,
          result: {
            error: false,
            ...result,
          },
        });
      } else {
        if (!result) {
          ctx.emit("fileApprove", {
            error: true,
            message: "Aprobación cancelada",
          });
          toasts.push({
            title: "Operación Fallida",
            content: "Aprobación cancelada",
            style: "error",
          });
          return;
        }

        const response = JSON.parse(result.response);
        if (
          response.estadoProceso ==
          "Token Inválido, ingrese con su cuenta de ciudadanía digital"
        ) {
          localStorage.removeItem("digital");
          ciudadaniaDigital.value = false;
        }
        ctx.emit("fileApprove", {
          documentId: viewSidebar.fileSelect.id,
          result,
          error: false,
        });
        if (props.toastEnabled) {
          toasts.push({
            title: "Operación Fallida",
            content: result.response.includes("Too Large")
              ? "El Archivo es demasiado grande"
              : result.message,
            style: "error",
          });
        }
      }
    };


    const onGetValueLegalCiudadaniaDigital = async (force = false) => {
      try {
        if (!props.viewSignature.agetic) return;
        if (!viewSidebar.fileSelect?.id) return;
        if (!props.tokenAgetic) return;

        const find = data_items.agetic.findIndex(
          (item) => item?.documentId === viewSidebar.fileSelect.id
        );
        if (find !== -1 && !force) return;
        data_items.ageticLoading = true;
        const result = await onObtenerValorLegalDocumentoAgeticApi({
          body: {
            idFiles: [viewSidebar.fileSelect.id],
            developmentEnvironment: props.modeAgetic,
            url: props.msAgetic,
            token: props.tokenAgetic,
          },
        });
        if (!result.error) {
          const index = data_items.agetic.findIndex(
            (item) => item.documentId === viewSidebar.fileSelect.id
          );
          if (index != -1) data_items.agetic[index] = result.response[0];
          else {
            data_items.agetic.push(...result.response?.data);
            aprobadores.value = result.response?.data[0]?.verification?.registros;
          }
        }
      } catch (e) {
        console.error("information agetic",e);
        if (e.message == "jwt expired") {
          toasts.push({
            title: "Operación Fallida",
            content: "Por favor recargar la lista de valores legale(AGETIC)",
            style: "error",
          });
          localStorage.removeItem("msTokenAgetic");
        } else {
          toasts.push({
            title: "Operación Fallida",
            content:
              "No se pudo obtener el valor legal del documento legal(AGETIC)",
            style: "error",
          });
        }
      } finally {
        data_items.ageticLoading = false;
      }
    };
    // #endregion servicios para ciudadania digital

    const onSelectFile = (file: any) => {
      if (file.id != viewSidebar.fileSelect.id) {
        viewSidebar.fileSelect = file;
        open();
      }
    };

    const filesInformation = ref<any>([]);
    const onLoadFiles = async () => {
      try {
        filesInformation.value = [];
        if (props.files.length == 0) return;
        for (const item of props.files) {
          item.pdf = props.waterMarkerActive
            ? generateUrlWaterMarker(
                props.msSeguridad,
                item.id,
                props.token,
                props.waterMarkerText
              )
            : item?.pdf
            ? item.pdf
            : `${props.msSeguridad}/api/v1/ms-files/ver/archivo/${item.id}/${props.token}`;
          filesInformation.value.push(item);
        }

        // if (props.enableInformationFile) {
        //   const fileIds: string[] = filesInformation.value
        //     .filter((data) => data?.id)
        //     .map((item: any) => item.id);
        //   if (fileIds.length > 0) {
        //     const result = await onGetInformationFilesApi({
        //       body: {
        //         msFilesIds: fileIds,
        //         url: props.msSeguridad,
        //         token: props.token,
        //       },
        //     });
        //     if (!result.error) {
        //       filesInformation.value = result.response.map((item) => {
        //         const pdf = props.files.find((tmp) => tmp.id == item.id)?.pdf;
        //         return {
        //           id: item.id,
        //           fileName: item.originalName,
        //           size: item.size,
        //           totalFirmadosAdsib: item.totalFirmadosAdsib,
        //           totalFirmadosAgetic: item.totalFirmadosAgetic,
        //           sePuedeAprobar: item.size < 2e7,
        //           pdf: props.waterMarkerActive
        //             ? generateUrlWaterMarker(
        //                 props.msSeguridad,
        //                 item.id,
        //                 props.token,
        //                 props.waterMarkerText
        //               )
        //             : pdf
        //             ? pdf
        //             : `${props.msSeguridad}/api/v1/ms-files/ver/archivo/${item.id}/${props.token}`,
        //         };
        //       });
        //       viewSidebar.fileSelect = filesInformation.value[0];
        //       open();
        //     }
        //   } else {
        //     toasts.push({
        //       title: "Operación Fallida",
        //       content: "Los Atributos del Array files debe contener ID",
        //       style: "error",
        //     });
        //   }
        // } else
        viewSidebar.fileSelect = filesInformation.value[0];
      } catch (e) {
        console.log(e);
      }
    };

    const modalFirma = ref(false);
    const passwordVisible = ref(false);
    const pin = ref("");
    const onModal = (val: boolean) => (modalFirma.value = val);

    const adsibEnabled = computed(() => {
      if (adsibInformation.value) {
        if (!adsibInformation.value.token.estado) return false;
        const fechaInicio = DateTime.fromISO(
          adsibInformation.value.contrato.fechaInicio
        );
        const fechaFin = DateTime.fromISO(
          adsibInformation.value.contrato.fechaFin
        );
        const actual = DateTime.now();
        if (!(actual >= fechaInicio && actual <= fechaFin)) return false;
        return true;
      }
      return false;
    });

    const onSyncFile = async () => {
      try {
        loadingAction.sync_file = true;
        const results = await Promise.all([
          onSincronizarValorLegalDocumentoAgeticApi({
            body: {
              idFile: viewSidebar.fileSelect.id,
              developmentEnvironment: props.modeAgetic,
              url: props.msAgetic,
              token: props.tokenAgetic,
            },
          }),
          onSincronizarValorLegalDocumentoAdsibApi({
            body: {
              idFile: viewSidebar.fileSelect.id,
              url: props.msAdsib,
              token: props.token,
            },
          }),
        ]);
        if (!results[0].error && !results[0].error) {
          toasts.push({
            title: "Operación Exitosa",
            content: "Documento sincronizado exitosamente",
            style: "success",
          });
        }
      } catch (e) {
        toasts.push({
          title: "Operación Fallida",
          content:
            "Hubo un problema al sincronizar las firmas, intente nuevamente",
          style: "error",
        });
      } finally {
        modalSync.value = false;
        loadingAction.sync_file = false;
      }
    };

    return {
      onSyncFile,
      modalSync,
      loadingAction,
      disabledButtonLegalComputed,
      adsibEnabled,
      adsibInformation,
      passwordVisible,
      filesInformation,
      pin,
      // #endregion variables para modal
      modalFirma,
      onModal,
      // #endregion variables para modal

      loader,
      data_items,
      // #region  function aprovar/firmar
      ciudadaniaDigital,
      onLoginCiudadaniaDigital,
      onFirmar,
      onAprobarCiudadania,
      // #endregion
      items,
      valorLegal,
      loading,
      pdfApp,
      showElem,
      slotProps,
      isToolbarHidden,
      isSidebarToolbarHidden,
      localTheme,
      defaultLocale,
      aprobadores,
      bindOpenHandler,
      viewSidebar,
      onSelectFile,
      onDeleteFile: (id: string) => {
        if (filesInformation.value.value == 0) return;
        const index = filesInformation.value.findIndex(
          (data: any) => data.id == id
        );
        if (index != -1) {
          filesInformation.value.splice(index, 1);
          if (
            viewSidebar.fileSelect.id == id &&
            filesInformation.value.length > 0
          ) {
            ctx.emit("deleteFile", {
              id,
            });
          }
        }
      },
      onOpenSidebar: (
        key: "files" | "signatures",
        section: "agetic" | "adsib" | undefined = undefined
      ) => {
        switch (key) {
          case "files":
            viewSidebar.files = !viewSidebar.files;
            break;
          case "signatures":
            const toggled =
              (section === "agetic" && valorLegal.agetic) ||
              (section === "adsib" && valorLegal.adsib);
            if (section == "agetic") {
              valorLegal.agetic = !valorLegal.agetic;
              valorLegal.adsib = false;
              onGetValueLegalCiudadaniaDigital();
            } else if (section == "adsib") {
              valorLegal.adsib = !valorLegal.adsib;
              valorLegal.agetic = false;
              onGetValueLegalAdsib();
            }
            if (toggled) viewSidebar.signatures = !viewSidebar.signatures;
            else viewSidebar.signatures = true;
            break;
        }
      },
      onLoadSignatarios: () => {
        if (valorLegal.agetic) onGetValueLegalCiudadaniaDigital(true);
        if (valorLegal.adsib) onGetValueLegalAdsib(true);
      },
      formatSizeUnits,
      DateTime,
      DATE_FORMAT_HUMAN,
      DATE_TIME_FULL_HUMAN,
      progressArg: '{ "progress": 0 }',
    };
  },
});
